<template>
  <div class="retention-message-form">
    <div class="type-banner">
      <span class="type-banner-icon">
        <i :class="typeMeta.icon"></i>
      </span>
      <div class="type-banner-text">
        <strong>{{ typeMeta.label }}</strong>
        <span>{{ typeMeta.summary }}</span>
      </div>
      <span v-if="!isNewMessage" class="type-banner-locked" title="Type is locked once a draft is saved.">
        <i class="fas fa-lock"></i> Locked
      </span>
    </div>

    <div class="form-group">
      <label>Name <span class="required">*</span></label>
      <input
        v-model="formData.name"
        :disabled="!nameEditable"
        placeholder="Internal label (e.g. weekly_winback_v1)"
        class="form-control"
      />
      <p v-if="approvedMode" class="hint">
        Approved messages are immutable on Apple's side; you can still update the internal name.
      </p>
    </div>

    <div class="form-group">
      <label>Language <span class="required">*</span></label>
      <select v-model="formData.language" :disabled="!languageEditable" class="form-control">
        <option value="" disabled>— Select a language —</option>
        <option v-for="loc in supportedLocales" :key="loc.code" :value="loc.code">
          {{ loc.label }} — {{ loc.code }}
        </option>
      </select>
      <p v-if="languageEditable" class="hint">
        Required. Drives grouping and locale fan-out. Locked once the message is submitted.
      </p>
      <p v-else class="hint">
        <i class="fas fa-lock"></i> Language is locked once set.
      </p>
    </div>

    <div class="form-group">
      <label>Header <span class="required">*</span></label>
      <input
        v-model="formData.header"
        :disabled="contentLocked"
        :maxlength="HEADER_MAX"
        placeholder="Message header (max 66 chars)"
        class="form-control"
      />
      <span class="char-count">{{ formData.header.length }}/{{ HEADER_MAX }}</span>
    </div>

    <div class="form-group">
      <label>Body <span class="required">*</span></label>
      <textarea
        v-model="formData.body"
        :disabled="contentLocked"
        :maxlength="BODY_MAX"
        rows="4"
        placeholder="Message body (max 144 chars)"
        class="form-control"
      ></textarea>
      <span class="char-count">{{ formData.body.length }}/{{ BODY_MAX }}</span>
    </div>

    <!-- Main hero image (image / bulleted_image) -->
    <template v-if="hasMainImage">
      <div class="form-group">
        <label>Image <span class="required">*</span></label>
        <select
          v-model="formData.imageIdentifier"
          :disabled="contentLocked"
          class="form-control"
        >
          <option :value="null" disabled>Select an APPROVED image</option>
          <option
            v-for="img in mainImageOptions"
            :key="img.imageIdentifier"
            :value="img.imageIdentifier"
          >
            {{ imageOptionLabel(img) }}
          </option>
        </select>
        <p class="hint" v-if="mainImageOptions.length === 0">
          No APPROVED <strong>main images</strong> yet. Upload a hero image
          (3840 wide) and tag it as <strong>Main image</strong> in the Images tab.
        </p>
      </div>

      <div class="form-group">
        <label>Alt Text <span class="required">*</span></label>
        <input
          v-model="formData.altText"
          :disabled="contentLocked"
          :maxlength="ALT_TEXT_MAX"
          placeholder="Accessibility text (max 150 chars)"
          class="form-control"
        />
        <span class="char-count">{{ (formData.altText || '').length }}/{{ ALT_TEXT_MAX }}</span>
      </div>

      <div class="form-group">
        <label>Header position</label>
        <div class="pill-row">
          <button
            v-for="opt in headerPositionOptions"
            :key="opt.value"
            type="button"
            class="pill"
            :class="{ 'pill-active': formData.headerPosition === opt.value }"
            :disabled="contentLocked"
            @click="formData.headerPosition = opt.value"
          >
            {{ opt.label }}
          </button>
        </div>
        <p class="hint">
          Where the header text sits relative to the hero image.
        </p>
      </div>
    </template>

    <!-- Bullets (bulleted / bulleted_image) -->
    <retention-message-bullet-editor
      v-if="hasBullets"
      v-model="formData.bulletPoints"
      :disabled="contentLocked"
    />

    <div v-if="errors.length" class="error-list">
      <p v-for="(error, index) in errors" :key="index" class="error">{{ error }}</p>
    </div>

    <div v-if="!viewOnly" class="form-actions">
      <button @click="handleSave" class="btn btn-primary" :disabled="saving" type="button">
        <span v-if="saving" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
        <span v-else>{{ approvedMode ? 'Save' : 'Save Draft' }}</span>
      </button>
      <button @click="$emit('cancel')" class="btn btn-ghost" type="button">
        Cancel
      </button>
    </div>
  </div>
</template>

<script>
import { permissionsMixin } from '../../mixins/permissions.mixin';
import { SUPPORTED_LOCALES } from '../../constants/retentionLocales';
import {
  MESSAGE_TYPES,
  TYPES_WITH_MAIN_IMAGE,
  TYPES_WITH_BULLETS,
  HEADER_MAX,
  BODY_MAX,
  ALT_TEXT_MAX,
  BULLET_TEXT_MAX,
  BULLET_ALT_TEXT_MAX,
  MAX_BULLETS,
  HEADER_POSITIONS,
  META_BY_TYPE
} from '../../constants/retentionMessageTypes';
import RetentionMessageBulletEditor from './MessageBulletEditor.vue';

const blankBullet = () => ({ text: '', altText: '', imageIdentifier: '' });

export default {
  name: 'RetentionMessageForm',
  mixins: [permissionsMixin],
  components: {
    RetentionMessageBulletEditor
  },
  props: {
    message: {
      type: Object,
      default: () => ({
        name: '',
        header: '',
        body: '',
        messageType: 'text',
        imageIdentifier: null,
        altText: '',
        messageState: 'draft'
      })
    },
    // viewOnly: form is fully read-only (e.g. user lacks edit perm, or message is in
    // a non-editable state like PENDING). Note: APPROVED is NOT viewOnly — the parent
    // passes approvedMode instead so the name field stays editable.
    viewOnly: {
      type: Boolean,
      default: false
    },
    // approvedMode: content fields are locked (Apple-side immutable) but `name` stays
    // editable as an internal label.
    approvedMode: {
      type: Boolean,
      default: false
    },
    // True when this is a brand-new draft (no id yet). Hides the "Locked" indicator.
    isNewMessage: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      formData: this.toFormData(this.message),
      errors: [],
      saving: false,
      HEADER_MAX,
      BODY_MAX,
      ALT_TEXT_MAX,
      BULLET_TEXT_MAX,
      BULLET_ALT_TEXT_MAX,
      MAX_BULLETS,
      supportedLocales: SUPPORTED_LOCALES,
      headerPositionOptions: [
        { value: HEADER_POSITIONS.ABOVE_BODY, label: 'Above body (default)' },
        { value: HEADER_POSITIONS.ABOVE_IMAGE, label: 'Above image' }
      ]
    };
  },
  computed: {
    typeMeta() {
      return META_BY_TYPE[this.formData.messageType] || META_BY_TYPE[MESSAGE_TYPES.TEXT];
    },
    hasMainImage() {
      return TYPES_WITH_MAIN_IMAGE.has(this.formData.messageType);
    },
    hasBullets() {
      return TYPES_WITH_BULLETS.has(this.formData.messageType);
    },
    approvedImages() {
      return this.$store.getters['retention/approvedImages'] || [];
    },
    mainImageOptions() {
      // Main slot uses Apple's FULL_SIZE format (3840 wide). Legacy images
      // without `usage` are physically FULL_SIZE — surface them as 'main'.
      return this.approvedImages.filter((img) => img.usage !== 'bullet');
    },
    nameEditable() {
      return !this.viewOnly;
    },
    contentLocked() {
      return this.viewOnly || this.approvedMode;
    },
    // Editable only while draft; immutable once set (a legacy empty language may be set once).
    languageEditable() {
      if (this.viewOnly) return false;
      return this.isNewMessage || this.message?.messageState === 'draft' || !this.message?.language;
    }
  },
  watch: {
    message: {
      deep: true,
      handler(newVal) {
        this.formData = this.toFormData(newVal);
        this.$emit('change', this.snapshot());
      }
    },
    formData: {
      deep: true,
      handler() {
        this.$emit('change', this.snapshot());
      }
    }
  },
  mounted() {
    this.$emit('change', this.snapshot());
  },
  methods: {
    toFormData(msg) {
      const messageType = msg?.messageType || 'text';
      const bullets = Array.isArray(msg?.bulletPoints) && msg.bulletPoints.length > 0
        ? msg.bulletPoints.map((b) => ({
            text: b.text || '',
            altText: b.altText || '',
            imageIdentifier: b.imageIdentifier || ''
          }))
        : (TYPES_WITH_BULLETS.has(messageType) ? [blankBullet()] : []);
      return {
        name: msg?.name || '',
        messageType,
        header: msg?.header || '',
        body: msg?.body || '',
        imageIdentifier: msg?.imageIdentifier || null,
        altText: msg?.altText || '',
        headerPosition: msg?.headerPosition || HEADER_POSITIONS.ABOVE_BODY,
        bulletPoints: bullets,
        language: msg?.language || ''
      };
    },
    snapshot() {
      // Emit shallow clone so parent's deep-watch doesn't echo back.
      return {
        ...this.formData,
        bulletPoints: this.formData.bulletPoints.map((b) => ({ ...b }))
      };
    },
    imageOptionLabel(img) {
      const usage = img.usage === 'bullet' ? 'Bullet' : 'Main';
      const name = img.name || `${img.imageIdentifier.slice(0, 8)}…`;
      return `${name}  ·  ${usage}  (${img.imageState})`;
    },
    validateForm() {
      this.errors = [];
      if (!this.formData.name.trim()) {
        this.errors.push('Name is required');
      }
      if (!this.formData.language) {
        this.errors.push('Language is required');
      }
      if (this.approvedMode) {
        return this.errors.length === 0;
      }
      if (!this.formData.header.trim()) {
        this.errors.push('Header is required');
      } else if (this.formData.header.length > HEADER_MAX) {
        this.errors.push(`Header must be ${HEADER_MAX} characters or fewer`);
      }
      if (!this.formData.body.trim()) {
        this.errors.push('Body is required');
      } else if (this.formData.body.length > BODY_MAX) {
        this.errors.push(`Body must be ${BODY_MAX} characters or fewer`);
      }
      if (this.hasMainImage) {
        if (!this.formData.imageIdentifier) {
          this.errors.push('Pick an APPROVED image');
        }
        if (!this.formData.altText?.trim()) {
          this.errors.push('Alt text is required for image messages');
        } else if (this.formData.altText.length > ALT_TEXT_MAX) {
          this.errors.push(`Alt text must be ${ALT_TEXT_MAX} characters or fewer`);
        }
      }
      if (this.hasBullets) {
        const bullets = this.formData.bulletPoints || [];
        if (bullets.length === 0) {
          this.errors.push('At least one bullet point is required');
        } else if (bullets.length > MAX_BULLETS) {
          this.errors.push(`At most ${MAX_BULLETS} bullets are allowed`);
        }
        bullets.forEach((b, i) => {
          if (!b.text?.trim()) this.errors.push(`Bullet #${i + 1}: text is required`);
          else if (b.text.length > BULLET_TEXT_MAX) {
            this.errors.push(`Bullet #${i + 1}: text must be ${BULLET_TEXT_MAX} characters or fewer`);
          }
          if (!b.altText?.trim()) this.errors.push(`Bullet #${i + 1}: alt text is required`);
          else if (b.altText.length > BULLET_ALT_TEXT_MAX) {
            this.errors.push(`Bullet #${i + 1}: alt text must be ${BULLET_ALT_TEXT_MAX} characters or fewer`);
          }
          if (!b.imageIdentifier) this.errors.push(`Bullet #${i + 1}: pick an APPROVED icon image`);
        });
      }
      return this.errors.length === 0;
    },
    handleSave() {
      if (!this.validateForm()) return;
      this.saving = true;
      const language = this.formData.language || null;
      if (this.approvedMode) {
        const out = { name: this.formData.name.trim() };
        // Immutable once set; include only for the one-time legacy set.
        if (this.languageEditable) out.language = language;
        this.$emit('save', out);
        return;
      }
      const payload = {
        name: this.formData.name.trim(),
        messageType: this.formData.messageType,
        header: this.formData.header,
        body: this.formData.body,
        language
      };
      if (this.hasMainImage) {
        payload.imageIdentifier = this.formData.imageIdentifier;
        payload.altText = this.formData.altText;
        if (this.formData.headerPosition && this.formData.headerPosition !== HEADER_POSITIONS.ABOVE_BODY) {
          payload.headerPosition = this.formData.headerPosition;
        }
      }
      if (this.hasBullets) {
        payload.bulletPoints = this.formData.bulletPoints.map((b) => ({
          text: b.text,
          altText: b.altText,
          imageIdentifier: b.imageIdentifier
        }));
      }
      this.$emit('save', payload);
    },
    resetSaving() {
      this.saving = false;
    }
  }
};
</script>

<style scoped>
.retention-message-form {
  color: rgba(255, 255, 255, 0.88);
}

.type-banner {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  background: rgba(3, 179, 218, 0.08);
  border: 1px solid rgba(3, 179, 218, 0.25);
  padding: 0.7rem 0.95rem;
  border-radius: 8px;
  margin-bottom: 1.2rem;
}

.type-banner-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background: rgba(3, 179, 218, 0.18);
  color: #6dd0ea;
  font-size: 0.95rem;
  flex-shrink: 0;
}

.type-banner-text {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  flex: 1;
  min-width: 0;
}

.type-banner-text strong {
  color: #fff;
  font-size: 0.95rem;
}

.type-banner-text span {
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.78rem;
}

.type-banner-locked {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  color: rgba(255, 255, 255, 0.55);
  font-size: 0.74rem;
  font-weight: 500;
  white-space: nowrap;
}

.type-banner-locked i {
  font-size: 0.7rem;
}

.form-group {
  margin-bottom: 1.1rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.45rem;
  font-weight: 500;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.75);
  letter-spacing: 0.02em;
}

.retention-message-form .form-control {
  width: 100%;
  padding: 0.55rem 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  font-size: 0.9rem;
  background: rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.92);
  transition: border-color 0.15s ease, background-color 0.15s ease;
}

.retention-message-form .form-control::placeholder {
  color: rgba(255, 255, 255, 0.3);
  opacity: 1;
}

.retention-message-form .form-control:disabled {
  background-color: rgba(255, 255, 255, 0.02);
  color: rgba(255, 255, 255, 0.45);
  cursor: not-allowed;
}

.retention-message-form .form-control:focus {
  outline: none;
  border-color: #03B3DA;
  background: rgba(255, 255, 255, 0.06);
  box-shadow: 0 0 0 3px rgba(3, 179, 218, 0.15);
}

.retention-message-form select.form-control {
  appearance: none;
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3e%3cpath fill='%23ffffff' fill-opacity='0.5' d='M6 8L0 0h12z'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  padding-right: 2rem;
}

.retention-message-form select.form-control option {
  background: #1a2332;
  color: #fff;
}

.retention-message-form textarea.form-control {
  resize: vertical;
  min-height: 96px;
}

.char-count {
  display: block;
  text-align: right;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.45);
  margin-top: 0.3rem;
  font-variant-numeric: tabular-nums;
}

.required {
  color: #ff6b6b;
}

.hint {
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.5);
  margin: 0.4rem 0 0;
}

.hint strong {
  color: rgba(255, 255, 255, 0.78);
  font-weight: 600;
}

.pill-row {
  display: flex;
  gap: 0.4rem;
  flex-wrap: wrap;
}

.pill {
  padding: 0.4rem 0.95rem;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.75);
  font-size: 0.82rem;
  cursor: pointer;
  transition: all 0.12s ease;
  font-weight: 500;
}

.pill:hover:not(:disabled) {
  border-color: #03B3DA;
  color: #fff;
}

.pill-active {
  background: rgba(3, 179, 218, 0.25);
  border-color: #03B3DA;
  color: #fff;
}

.pill:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.error-list {
  margin-bottom: 1rem;
  padding: 0.75rem 1rem;
  background: rgba(231, 76, 60, 0.08);
  border: 1px solid rgba(231, 76, 60, 0.2);
  border-radius: 6px;
}

.error {
  color: #ff8a80;
  font-size: 0.85rem;
  margin: 0.15rem 0;
}

.form-actions {
  display: flex;
  gap: 0.6rem;
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.retention-message-form .btn {
  padding: 0.55rem 1.4rem;
  border-radius: 6px;
  font-size: 0.88rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease, transform 0.05s ease;
  border: 1px solid transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
}

.retention-message-form .btn:active {
  transform: translateY(1px);
}

.retention-message-form .btn-primary {
  background-color: #03B3DA;
  color: #fff;
  border-color: #03B3DA;
}

.retention-message-form .btn-primary:hover:not(:disabled) {
  background-color: #02a0c2;
  border-color: #02a0c2;
}

.retention-message-form .btn-primary:disabled {
  background-color: rgba(3, 179, 218, 0.3);
  border-color: transparent;
  color: rgba(255, 255, 255, 0.6);
  cursor: not-allowed;
}

.retention-message-form .btn-ghost {
  background: transparent;
  color: rgba(255, 255, 255, 0.75);
  border: 1px solid rgba(255, 255, 255, 0.18);
}

.retention-message-form .btn-ghost:hover {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.3);
  color: #fff;
}

.bullet-editor {
  margin-bottom: 1.1rem;
}
</style>
